import { Button, Icons } from "@win-reality/react-ui";
import { useLocation, useMatches, useNavigate } from "react-router-dom";

import { ROUTES } from "@/lib/routes";
import type { RouteHandle } from "@/types/routes";

/**
 * Back button component for navbar navigation.
 * Navigates to parent route or falls back to dashboard.
 * Hidden on dashboard (top-level) and routes with hideBackButton handle.
 */
const BackButton: React.FC = () => {
  const navigate = useNavigate();
  const matches = useMatches();
  const location = useLocation();

  const currentMatch =
    [...matches].reverse().find((match) => match.handle != null) ??
    matches[matches.length - 1];
  const handle = currentMatch?.handle as RouteHandle | undefined;

  const handleBack = () => {
    if (handle?.backPath === "history") {
      navigate(-1);
      return;
    }
    if (typeof handle?.backPath === "string") {
      navigate(handle.backPath);
      return;
    }
    // Strip N path segments (default 1). e.g. with 2: /a/b/c/d → /a/b
    const steps = typeof handle?.backPath === "number" ? handle.backPath : 1;
    let parentPath = location.pathname;
    for (let i = 0; i < steps; i++) {
      parentPath = parentPath.replace(/\/[^/]+\/?$/, "");
    }
    navigate(parentPath || ROUTES.DASHBOARD);
  };

  // Hide on top-level pages (no meaningful parent to go back to)
  const parentPath = location.pathname.replace(/\/[^/]+\/?$/, "");
  const isTopLevel = !parentPath || parentPath === "/";

  // Hide when navigated from sidebar (top-level nav context)
  const fromSidebar = (location.state as { fromSidebar?: boolean } | null)
    ?.fromSidebar;

  if (isTopLevel || handle?.hideBackButton || fromSidebar) {
    return null;
  }

  return (
    <Button variant="secondary-flat-dark" size="icon-mini" onClick={handleBack}>
      <Icons.ArrowLeftMini />
    </Button>
  );
};

export default BackButton;
